import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import AdminDashboard from './components/AdminDashboard';
import SuccessBidsPage from './components/SuccessBidsPage';
import SubUserRegister from './components/SubUserRegister';
import MoreSettings from './components/MoreSettings';
import ManageSubscription from './components/ManageSubscription';
import SimpleAddTemplateForm from './components/aiTemplates/SimpleAddTemplateForm';
import MainLayout from './components/MainLayout';
import Dashboard from './components/Dashboard';
import ProposalTemplates from './components/ProposalTemplates';
import { FirebaseAuthProvider } from './contexts/FirebaseAuthContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ModalProvider } from './contexts/ModalContext';
import { useFirebaseAuth } from './contexts/FirebaseAuthContext';
import Home from './pages/home';
import UpdateProfile from './components/UpdateProfile';
import MainDashboard from './pages/MainDashboard';
import Projects from './pages/Projects';
import ProjectFilters from './components/ProjectFilters';
import Notifications from './components/Notifications';
import { initializePaddle } from '@paddle/paddle-js';
import { useNavigate } from 'react-router-dom';
import { getAccessToken, isJwtExpired, scheduleLogoutOnExpiry, clearAccessToken } from './utils/accessToken';
// simple protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user: fbUser, loading } = useFirebaseAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div>Loading...</div></div>;
  if (!fbUser) return <Navigate to="/login" replace />;
  if (requireAdmin && fbUser.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

// PublicRoute: if already logged in + token valid, redirect to dashboard
const PublicRoute = ({ children }) => {
  const { user: fbUser, loading } = useFirebaseAuth();
  const token = getAccessToken();
  if (loading) return <div className="flex items-center justify-center h-screen"><div>Loading...</div></div>;
  if (fbUser && token && !isJwtExpired(token)) return <Navigate to="/dashboard" replace />;
  return children;
};
function App() {

  useEffect(() => {
    initializePaddle({
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      environment: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'sandbox' : 'production'
    });
  }, []);

  useEffect(() => {
    if (!window.Paddle) return;
    window.Paddle.Initialize({
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      seller: Number(process.env.REACT_APP_PADDLE_VENDOR_ID),
    });
  }, []);

  // read current auth loading state (keeps same gating as before)
  const { user: fbUser, loading: fbLoading } = useFirebaseAuth();

  // AuthExpiry component runs inside Router so useNavigate is valid
  const AuthExpiry = () => {
    const { logout } = useFirebaseAuth();
    const navigate = useNavigate();
    useEffect(() => {
      const token = getAccessToken();
      if (!token) return;
      if (isJwtExpired(token)) {
        clearAccessToken();
        try { logout?.(); } catch (e) { /* ignore */ }
        navigate('/login', { replace: true });
        return;
      }
      const to = scheduleLogoutOnExpiry(token, () => {
        clearAccessToken();
        try { logout?.(); } catch (e) { /* ignore */ }
        navigate('/login', { replace: true });
      });
      return () => { if (to) clearTimeout(to); };
    }, [logout, navigate]);
    return null;
  };

  if (fbLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#F8F9FA' }}>
        <div style={{ fontSize: 24, marginBottom: 10 }}></div>
        <div style={{ color: '#6C757D' }}>Loading...</div>
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <Router>
        <AuthExpiry />
        <FirebaseAuthProvider>
          <AuthProvider>
            <NotificationProvider>
              <ModalProvider>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<PublicRoute><LoginForm /></PublicRoute>} />
                  <Route path="/register" element={<PublicRoute><RegisterForm /></PublicRoute>} />
                  <Route
                    path="/maindashboard"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <MainDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminDashboard /></ProtectedRoute>} />
                  {/* App shell: MainLayout keeps Navbar/Sidebar; nested pages render inside it */}
                  <Route path="/dashboard" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                    <Route index element={<Dashboard />} />
                    <Route path="projects" element={<Projects />} />
                    <Route path="subusers" element={<SubUserRegister parentUid={fbUser?.uid} />} />
                    <Route path="bids" element={<SuccessBidsPage />} />
                    <Route path="proposal-templates" element={<ProposalTemplates />} />
                    <Route path="update-profile" element={<UpdateProfile />} />
                    {/* More settings - shows cards */}
                    <Route path="more-settings" element={<MoreSettings />} />
                    <Route path="manage-subscription" element={<ManageSubscription />} />
                    {/* Project Filters - shows all 4 filters at once */}
                    <Route path="more-settings/project-filters" element={<ProjectFilters />} />
                    {/* Individual filter pages */}
                    <Route path="more-settings/:tab" element={<MoreSettings />} />
                    <Route path="more-settings/notifications" element={<Notifications />} />
                    <Route path="ai-templates" element={<Navigate to="/dashboard/proposal-templates" replace />} />
                    <Route path="ai-templates/add" element={<SimpleAddTemplateForm />} />
                  </Route>
                  {/* legacy redirects (optional) */}
                  <Route path="/ai-templates/*" element={<Navigate to="/dashboard/ai-templates" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ModalProvider>
            </NotificationProvider>
          </AuthProvider>
        </FirebaseAuthProvider>
      </Router>
    </ErrorBoundary>
  );
}
export default App;